import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { useOrderHistory } from '../../../contexts/OrderHistoryContext';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import ThemeSwitcher from '../ThemeSwitcher/ThemeSwitcher';
import Icon from '../Icon/Icon';
import Button from '@/components/atoms/Button/Button';

interface MobileMenuProps {
  logoSrc: string;
  isActive: (path: string) => boolean;
  variant?: 'public' | 'authenticated';
  onOpenLogin?: () => void;
  onOpenRegister?: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ 
  logoSrc, 
  isActive, 
  variant = 'public',
  onOpenLogin,
  onOpenRegister
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, logout } = useAuth();
  const { cart } = useCart();
  const { hasOrders } = useOrderHistory();
  const [isOpen, setIsOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside or on escape key
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  const cartItemCount = cart?.items?.length || 0;

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      <button
        className="public-layout__mobile-menu-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle menu"
        aria-expanded={isOpen}
      >
        <span className={`public-layout__hamburger ${isOpen ? 'public-layout__hamburger--open' : ''}`}>
          <span></span>
          <span></span>
          <span></span>
        </span>
      </button>

      {/* Mobile Menu Overlay */}
      {isOpen && (
        <div 
          className="public-layout__mobile-overlay" 
          onClick={() => setIsOpen(false)} 
        />
      )}

      {/* Mobile Menu Dropdown */}
      {isOpen && (
        <div className="public-layout__mobile-menu" ref={mobileMenuRef}>
          <div className="public-layout__mobile-menu-content">
            {/* Header del menú con logo y botón cerrar */}
            <div className="public-layout__mobile-menu-header">
              <div className="public-layout__mobile-menu-logo">
                <img
                  src={logoSrc}
                  alt="Prior"
                  className="public-layout__mobile-menu-logo-img"
                />
              </div>
              <button
                className="public-layout__mobile-menu-close"
                onClick={() => setIsOpen(false)}
                aria-label="Cerrar menú"
              >
                <Icon name="cerrar" size={20} alt="Cerrar menú" />
              </button>
            </div>

            {/* Controles en una fila */}
            <div className="public-layout__mobile-controls">
              <div className="public-layout__mobile-control-item">
                <label>{t('navigation.language')}</label>
                <LanguageSwitcher />
              </div>
              <div className="public-layout__mobile-control-item">
                <label>{t('navigation.toggleTheme')}</label>
                <ThemeSwitcher variant="mobile" />
              </div>
            </div>

            {/* Separador */}
            <div className="public-layout__mobile-separator"></div>

            {/* Opciones de usuario */}
            <div className="public-layout__mobile-user-actions">
              
              <Button 
                variant="secondary"
                size="md"
                className="public-layout__mobile-action-btn public-layout__mobile-action-btn--events"
                onClick={() => handleNavigation('/events')}
              ><Icon name="eventos" size={20} alt={t('navigation.events')} />
                {t('navigation.events')}
                
              </Button>

            {/* Opciones de navegación */}
            <div className="public-layout__mobile-nav-options">
              {/* Historial de pedidos (solo si hay pedidos) */}
              {hasOrders && (
                <Button 
                  variant="primary" 
                  size="md" 
                  onClick={() => handleNavigation('/order-history')}
                  className="public-layout__mobile-action-btn public-layout__mobile-action-btn--orders"
                >
                  <span className="public-layout__mobile-nav-icon">
                    <Icon name="pedidos" size={20} alt={t('navigation.orderHistory')} />
                  </span>
                  <span>{t('navigation.orderHistory')}</span>
                </Button>
              )}
            </div>
              
{/* Separador */}
            <div className="public-layout__mobile-separator"></div>

              {isAuthenticated ? (
                <>
                  <Button
                    variant="secondary"
                    size="md"
                    className="public-layout__mobile-action-btn public-layout__mobile-action-btn--dashboard"
                    onClick={() => handleNavigation('/dashboard')}
                  >
                    {t('navigation.dashboard')}
                    
                  </Button>
                  <Button
                  variant="secondary"
                    size="md"
                    className="public-layout__mobile-action-btn public-layout__mobile-action-btn--logout"
                    onClick={handleLogout}
                  >
                    {t('navigation.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                  variant="secondary"
                    size="md"
                    className="public-layout__mobile-action-btn public-layout__mobile-action-btn--login"
                    onClick={() => {
                      setIsOpen(false);
                      if (onOpenLogin) {
                        onOpenLogin();
                        return;
                      }
                      handleNavigation('/login');
                    }}
                  >
                    {t('navigation.login')}
                  </Button>
                  <Button
                  variant="secondary"
                    size="md"
                    className="public-layout__mobile-action-btn public-layout__mobile-action-btn--register"
                    onClick={() => {
                      setIsOpen(false);
                      if (onOpenRegister) {
                        onOpenRegister();
                        return;
                      }
                      handleNavigation('/register');
                    }}
                  >
                    {t('navigation.register')}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MobileMenu;
